import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { X, FileText, CheckSquare, Square, Sparkle, Warning } from "@phosphor-icons/react";

// Combined char cap for injecting selected files in FULL. Mirrors the backend
// BUDDY_SILO_CONTEXT_CAP_CHARS — over this we suggest the Tier-1-core + scoped
// smart-search (RAG) path, which requires at least one Tier 1 file (Randy, Jul 2026).
const FULL_CAP_CHARS = 600000;
const TIER_LABEL = { 1: "Tier 1 · Core", 2: "Tier 2 · Library", 3: "Tier 3 · Deep library" };

// The AI Assistant "File" mode picker: choose one or many files (any tier) to
// pair with the world LLM instead of the whole silo. On Save it size-checks the
// selection; an oversized selection pops a CTA that requires a Tier 1 anchor and
// switches to scoped smart-search.
export default function BuddyFilePicker({ siloId, open, initialSelected = [], onClose, onSave }) {
  const [docs, setDocs] = useState(null);
  const [sel, setSel] = useState(new Set(initialSelected));
  const [largeCta, setLargeCta] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSel(new Set(initialSelected));
    setLargeCta(false);
    let cancel = false;
    (async () => {
      setDocs(null);
      try {
        const r = await api.get(`/silos/${siloId}/docs`);
        if (!cancel) setDocs((r.data || []).filter((d) => d.enabled !== false));
      } catch { if (!cancel) setDocs([]); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siloId]);

  const byTier = useMemo(() => {
    const g = { 1: [], 2: [], 3: [] };
    (docs || []).forEach((d) => { (g[d.tier] || g[3]).push(d); });
    return g;
  }, [docs]);

  const selectedDocs = useMemo(() => (docs || []).filter((d) => sel.has(d.id)), [docs, sel]);
  const totalChars = selectedDocs.reduce((a, d) => a + (d.char_count || 0), 0);
  const hasTier1 = selectedDocs.some((d) => d.tier === 1);
  const oversized = totalChars > FULL_CAP_CHARS;

  const toggle = (id) => {
    setLargeCta(false);
    setSel((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const clearAll = () => { setLargeCta(false); setSel(new Set()); };

  const commit = () => {
    const ids = Array.from(sel);
    onSave(ids, { count: ids.length, chars: totalChars, mode: oversized ? "rag" : "full" });
    onClose();
  };

  const handleSave = () => {
    if (sel.size === 0) return;
    if (!oversized) { commit(); return; }
    // Oversized: need a Tier 1 anchor for the scoped smart-search path.
    if (!largeCta) { setLargeCta(true); return; }
  };

  const ctaOk = () => {
    if (hasTier1) { commit(); return; }
    // No Tier 1 anchor — bounce back to the picker to fix the selection.
    setLargeCta(false);
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="buddy-file-picker">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[86dvh]">
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-[#EDEBE6] flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#1C1B1A] text-white flex items-center justify-center shrink-0">
            <FileText size={17} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif-display text-lg leading-tight text-[#1C1B1A]">Load specific files</h3>
            <p className="text-[12px] text-[#57534E] mt-0.5">Pick one or more files to pair with the assistant — it'll use only these, not the whole silo.</p>
          </div>
          <button onClick={onClose} data-testid="buddy-file-picker-close" className="text-[#8A8780] hover:text-[#1C1B1A] p-1 -mr-1"><X size={18} /></button>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {docs === null ? (
            <div className="py-10 text-center text-[13px] text-[#8A8780]">Loading files…</div>
          ) : docs.length === 0 ? (
            <div className="py-10 text-center text-[13px] text-[#8A8780]">This silo has no files yet.</div>
          ) : (
            [1, 2, 3].map((t) => (
              byTier[t].length > 0 && (
                <div key={t} className="mb-4" data-testid={`buddy-file-tier-${t}`}>
                  <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A6D3B] font-semibold mb-1.5">{TIER_LABEL[t]}</div>
                  <div className="space-y-1">
                    {byTier[t].map((d) => {
                      const checked = sel.has(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => toggle(d.id)}
                          data-testid={`buddy-file-item-${d.id}`}
                          className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-colors ${checked ? "bg-[#F1F5F1]" : "hover:bg-[#F7F6F2]"}`}
                        >
                          <span data-testid={`buddy-file-check-${d.id}`} className={checked ? "text-[#2F6B3A]" : "text-[#B7B4AD]"}>
                            {checked ? <CheckSquare size={18} weight="fill" /> : <Square size={18} />}
                          </span>
                          <span className="flex-1 min-w-0 text-[13px] text-[#1C1B1A] truncate">{d.title || "Untitled"}</span>
                          <span className="text-[10px] text-[#A8A59E] tabular-nums shrink-0">{Math.max(1, Math.round((d.char_count || 0) / 1000))}k</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )
            ))
          )}
        </div>

        {/* Oversized CTA */}
        {largeCta && (
          <div className="px-5 py-3 bg-[#FBF3E6] border-t border-[#EAD9B8]" data-testid="buddy-file-large-cta">
            <div className="flex items-start gap-2 text-[12.5px] text-[#6B5324]">
              <Warning size={16} weight="fill" className="shrink-0 mt-px text-[#B8860B]" />
              <div>
                <p><strong>That's a large selection.</strong> I'll use your <strong>Tier 1</strong> file(s) as the core and smart-search the rest. <strong>At least one Tier 1 file must be selected.</strong></p>
                {!hasTier1 && <p className="mt-1 text-[#8A2E2E]">No Tier 1 file selected yet — pick one, or trim your Tier 2/3 selection to fit.</p>}
              </div>
            </div>
            <div className="flex gap-2 mt-2.5">
              <button onClick={() => setLargeCta(false)} data-testid="buddy-file-large-back" className="flex-1 text-[12px] font-medium text-[#6B5324] py-1.5 rounded-md border border-[#D9C08A] hover:bg-white/60">Back</button>
              <button onClick={ctaOk} data-testid="buddy-file-large-ok" className={`flex-1 text-[12px] font-medium py-1.5 rounded-md text-white ${hasTier1 ? "bg-[#2F6B3A] hover:bg-[#285E33]" : "bg-[#B8860B] hover:bg-[#9E7309]"}`}>{hasTier1 ? "OK — use smart-search" : "OK"}</button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-3.5 bg-[#FAFAF7] border-t border-[#EDEBE6] flex items-center gap-3">
          <div className="flex-1 text-[12px] text-[#57534E]">
            <span data-testid="buddy-file-selected-count" className="font-medium">Selected: {sel.size}</span>
            {sel.size > 0 && <span className="text-[#A8A59E]"> · ~{Math.max(1, Math.round(totalChars / 1000))}k chars</span>}
            {sel.size > 0 && (
              <button onClick={clearAll} data-testid="buddy-file-clear-all" className="ml-2 text-[#8A2E2E] hover:underline">Clear all</button>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={sel.size === 0}
            data-testid="buddy-file-save"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-white px-4 py-2 rounded-lg bg-[#1C1B1A] hover:bg-[#000] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Sparkle size={14} weight="duotone" /> Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
